import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import { Customer, CreateCustomerData, CustomerFormData, CustomerType } from '../../types/customer';
import {
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  IdentificationIcon,
  DocumentIcon,
  XMarkIcon,
  PaperClipIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  PlusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStorage } from '../../hooks/useStorage';
import { Tooltip } from '../common/Tooltip';
import { Timestamp, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { formatDocumentDate } from 'utils/formatters';
import { db } from '../../lib/firebase';
import { DatePicker } from '../common/DatePicker';
import DocumentPreviewModal from '../preview/DocumentPreviewModal';
import { formatTaxNumber, formatRegistrationNumber } from '../../utils/validation';
import { toast } from 'react-hot-toast';
import { generateCustomerCode } from '../../utils/formatters';
import { useCustomers } from '../../hooks/useCustomers';
import BulkInvoicingPanel from './BulkInvoicingPanel';
import BulkPodUploader from './BulkPodUploader';
import GasPodsSection from './GasPodsSection';
import GasContractsSection from './GasContractsSection';
import GasMeterReadingsSection from './GasMeterReadingsSection';
import GasInvoicesSection from './GasInvoicesSection';
import GasNominationsSection from './GasNominationsSection';
import { getAllConsultants } from '../../services/consultantService';
import { Consultant } from '../../types/consultant';

// Draft storage constants
const DRAFT_STORAGE_KEY = 'customer_form_draft';
const DRAFT_TIMESTAMP_KEY = 'customer_form_draft_timestamp';

interface CustomerFormProps {
  onSubmit: (data: CreateCustomerData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Customer | null;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { organizations } = useOrganizations();
  const { t } = useLanguage();
  const { uploadFile, deleteFile } = useStorage();
  const { customers } = useCustomers();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocumentToEdit, setSelectedDocumentToEdit] = useState<any>(null);
  const [additionalPods, setAdditionalPods] = useState<number[]>([]);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [podSearchTerm, setPodSearchTerm] = useState('');
  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [selectedConsultantId, setSelectedConsultantId] = useState<string>('');
  const [showBulkPodUploader, setShowBulkPodUploader] = useState(false);

  // Egyszerű jelölés, hogy meglévő kapcsolat-e az ügyfél
  const [isExistingRelationship, setIsExistingRelationship] = useState(false);

  // Draft recovery state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState<Date | null>(null);

  // Additional billing emails
  const [newBillingEmail, setNewBillingEmail] = useState('');

  const handleAddBillingEmail = () => {
    const email = newBillingEmail.trim();
    if (!email) return;
    const current = watch('additionalBillingEmails') || [];
    if (current.includes(email)) {
      setNewBillingEmail('');
      return;
    }
    setValue('additionalBillingEmails', [...current, email], { shouldDirty: true });
    setNewBillingEmail('');
  };

  // Szakreferensek betöltése
  useEffect(() => {
    const loadConsultants = async () => {
      try {
        const data = await getAllConsultants();
        setConsultants(data);
      } catch (error) {
        console.error('Error loading consultants:', error);
      }
    };
    loadConsultants();
  }, []);

  // Kezdeti szakreferens beállítása
  useEffect(() => {
    if (initialData?.consultant) {
      setSelectedConsultantId(initialData.consultant.id);
    }
  }, [initialData]);

  const defaultValues: CustomerFormData = {
    type: 'organization' as CustomerType,
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    taxNumber: '',
    euVatNumber: '',
    registrationNumber: '',
    organizations: [],
    notes: '',
    documents: [],
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    contactPerson: {
      name: '',
      email: '',
      phone: '',
      position: '',
    },
    contractNumber: '',
    companyAddress: '',
    bankAccount: '',
    representativeName: '',
    billingEmail: '',
    additionalBillingEmails: [],
    financialContact: {
      name: '',
      email: '',
      phone: ''
    },
    consumptionData: {
      date: '',
      dueDate: '',
      power: '',
      profileNumber: '',
      consumption: ''
    },
    contractPeriod: {
      startDate: '',
      endDate: '',
      price: '',
      priceUnit: 'HUF/kWh',
      paymentCurrency: 'HUF',
      indexedPrice: false,
      spread: '',
      priceEUR: ''
    },
    offerPrice: {
      currency: 'HUF',
      price: 'HUF/kWh'
    },
    kat: {
      text: '',
      kat: 'kát nélkül'
    },
    ekrSetting: {
      type: 'standard',
      customPrice: ''
    },
    pod: {
      id: '',
      address: '',
      partnerId: '',
      postalCode: '',
      city: '',
      street: '',
      measurementType: '',
      annualConsumption: ''
    },
    additionalPods: []
  };

  const { register, handleSubmit, watch, setValue, reset, getValues } = useForm<CustomerFormData>({
    defaultValues: initialData || defaultValues
  });

  // Test localStorage accessibility
  useEffect(() => {
    try {
      localStorage.setItem('test_storage', 'test');
      console.log('localStorage elérhető:', localStorage.getItem('test_storage'));
      localStorage.removeItem('test_storage');
    } catch (error) {
      console.error('localStorage nem érhető el:', error);
    }
  }, []);

  // Draft recovery functions
  const saveDraft = (data: any) => {
    try {
      // Mentjük a teljes form adatot, hogy minden mező visszaállítható legyen
      const draftToSave = {
        ...data,
        // Biztosítsuk, hogy ezek a tömbök léteznek, ha hiányoznának
        documents: Array.isArray(data.documents) ? data.documents : [],
        additionalPods: Array.isArray(data.additionalPods) ? data.additionalPods : [],
        organizations: Array.isArray(data.organizations) ? data.organizations : [],
        // Ellenőrizzük a beágyazott objektumokat is
        address: data.address || {},
        contactPerson: data.contactPerson || {},
        financialContact: data.financialContact || {},
        consumptionData: data.consumptionData || {},
        contractPeriod: data.contractPeriod || {},
        offerPrice: data.offerPrice || {},
        kat: data.kat || {},
        pod: data.pod || {}
      };
      
      const draftString = JSON.stringify(draftToSave);
      console.log("Saving complete draft to localStorage:", draftToSave);
      console.log("Draft string length:", draftString.length);
      
      localStorage.setItem(DRAFT_STORAGE_KEY, draftString);
      localStorage.setItem(DRAFT_TIMESTAMP_KEY, new Date().toISOString());
      
      // Ellenőrizzük, hogy sikeres volt-e a mentés
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      console.log("Saved draft successfully:", !!savedDraft);
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const loadDraft = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
      
      console.log("Loading draft from localStorage");
      console.log("Draft exists:", !!draftData);
      console.log("Timestamp exists:", !!timestamp);
      
      if (draftData && timestamp) {
        try {
          const parsedData = JSON.parse(draftData);
          console.log("Parsed draft data:", parsedData);
          setDraftTimestamp(new Date(timestamp));
          
          // Biztosítsuk, hogy minden szükséges tulajdonság inicializálva legyen
          const safeData = {
            ...parsedData,
            // Biztosítsuk, hogy a documents tömb mindig létezzen
            documents: Array.isArray(parsedData.documents) ? parsedData.documents : [],
            // Biztosítsuk, hogy az additionalPods tömb mindig létezzen
            additionalPods: Array.isArray(parsedData.additionalPods) ? parsedData.additionalPods : []
          };
          
          console.log("Safe draft data:", safeData);
          return safeData;
        } catch (parseError) {
          console.error("Error parsing draft data:", parseError);
          return null;
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
    return null;
  };

  const clearDraft = () => {
    try {
      console.log("Clearing draft");
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      setHasDraft(false);
      setDraftTimestamp(null);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  };

  const restoreDraft = () => {
    try {
      const draftData = loadDraft();
      if (draftData) {
        console.log("Restoring draft data:", draftData);
        
        // Biztosítsuk, hogy minden szükséges tulajdonság inicializálva legyen
        const completeData = {
          ...defaultValues,
          ...draftData,
          // Biztosítsuk, hogy a tömbök mindig létezzenek
          documents: Array.isArray(draftData.documents) ? draftData.documents : [],
          additionalBillingEmails: Array.isArray(draftData.additionalBillingEmails) ? draftData.additionalBillingEmails : [],
          organizations: Array.isArray(draftData.organizations) ? draftData.organizations : [],
          // Biztosítsuk, hogy a beágyazott objektumok mindig létezzenek
          address: { ...defaultValues.address, ...(draftData.address || {}) },
          contactPerson: { ...defaultValues.contactPerson, ...(draftData.contactPerson || {}) },
          financialContact: { ...defaultValues.financialContact, ...(draftData.financialContact || {}) },
          consumptionData: { ...defaultValues.consumptionData, ...(draftData.consumptionData || {}) },
          contractPeriod: { ...defaultValues.contractPeriod, ...(draftData.contractPeriod || {}) },
          offerPrice: { ...defaultValues.offerPrice, ...(draftData.offerPrice || {}) },
          kat: { ...defaultValues.kat, ...(draftData.kat || {}) },
          pod: { ...defaultValues.pod, ...(draftData.pod || {}) },
          additionalPods: (draftData.additionalPods || []).map((ap: any) => ({
            id: '',
            address: '',
            partnerId: '',
            postalCode: '',
            city: '',
            street: '',
            measurementType: '',
            annualConsumption: '',
            ...ap
          }))
        };
        
        console.log("Complete data for restoration:", completeData);
        
        // Állítsuk be a form adatait
        reset(completeData);
        
        // Állítsuk vissza az additionalPods értékeit is
        if (Array.isArray(completeData.additionalPods)) {
          const podIndexes = completeData.additionalPods.map((_: any, index: number) => index);
          setAdditionalPods(podIndexes);
        } else {
          setAdditionalPods([]);
        }
        
        // A draftot nem töröljük, csak elrejtjük a banner-t.
        // Így ha a felhasználó véletlenül bezárja a modált, a draft megmarad.
        setHasDraft(false);
      }
    } catch (error) {
      console.error("Error restoring draft:", error);
      toast.error("Hiba történt a vázlat visszaállítása közben");
      // Hiba esetén se töröljük a draftot, mert az adatok értékesek
    }
  };

  // Draft recovery banner component
  const DraftRecoveryBanner = () => {
    if (!hasDraft || !draftTimestamp) return null;
    
    // Format the date for display
    const formattedDate = new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(draftTimestamp);
    
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <DocumentTextIcon className="h-5 w-5 text-amber-600 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Mentett vázlat elérhető</h3>
            <p className="text-xs text-amber-700">
              {`Egy mentett vázlat elérhető (${formattedDate}). Szeretné visszaállítani?`}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={restoreDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
            Visszaállítás
          </button>
          <button
            type="button"
            onClick={clearDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <XMarkIcon className="h-3.5 w-3.5 mr-1" />
            Elvetés
          </button>
        </div>
      </div>
    );
  };

  // Register documents field
  React.useEffect(() => {
    register('documents');
  }, [register]);

  // Reset form when initialData changes and check for draft when form opens
  React.useEffect(() => {
    if (isOpen) {
      console.log("Form opened, initialData:", initialData ? "exists" : "doesn't exist");
      
      // Közvetlenül ellenőrizzük a localStorage-ban tárolt piszkozatot
      try {
        const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
        const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
        
        console.log("Direct localStorage check - Draft exists:", !!draftData);
        console.log("Direct localStorage check - Timestamp exists:", !!timestamp);
        
        if (draftData && timestamp) {
          try {
            const parsedData = JSON.parse(draftData);
            console.log("Parsed draft data:", parsedData);
            
            // Ha nincs initialData (új ügyfél) és van piszkozat, beállítjuk a hasDraft-ot
            if (!initialData) {
              console.log("Setting hasDraft to true directly");
              setHasDraft(true);
              setDraftTimestamp(new Date(timestamp));
              
              // Biztosítsuk, hogy a documents és additionalPods tömbök léteznek
              if (!parsedData.documents) parsedData.documents = [];
              if (!parsedData.additionalPods) parsedData.additionalPods = [];
            }
          } catch (parseError) {
            console.error("Error parsing draft data:", parseError);
          }
        } else {
          console.log("No draft found in localStorage");
          setHasDraft(false);
        }
      } catch (localStorageError) {
        console.error("Error accessing localStorage:", localStorageError);
      }
      
      // Check for existing draft when form opens (a korábbi metódus megtartása is)
      if (!initialData) {
        const draftData = loadDraft();
        if (draftData) {
          console.log("Setting hasDraft to true");
          setHasDraft(true);
        } else {
          setHasDraft(false);
        }
      } else {
        // If editing existing customer, clear any drafts
        clearDraft();
      }
      
      reset(initialData || defaultValues);
      
      // Ha van initialData, akkor nem új ügyfélről van szó
      if (initialData) {
        setIsExistingRelationship(false);
      }

      // Ha van initialData és abban van additionalPods, akkor automatikusan beállítjuk
      if (initialData) {
        const formattedCustomer = initialData as unknown as CustomerFormData;
        if (formattedCustomer.additionalPods && Array.isArray(formattedCustomer.additionalPods)) {
          const podIndexes = formattedCustomer.additionalPods.map((_, index) => index);
          setAdditionalPods(podIndexes);
        } else {
          setAdditionalPods([]);
        }
      } else {
        setAdditionalPods([]);
      }
    }
  }, [initialData, reset, isOpen]);

  // Auto-save draft with a timer (only for new customers)
  useEffect(() => {
    if (!isOpen || initialData) return;
    
    console.log("Starting auto-save timer");
    
    // Azonnali mentés az űrlap megnyitásakor
    const saveData = () => {
      const currentValues = getValues();
      // Don't save empty forms
      if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
        console.log("Auto-saving draft via timer");
        saveDraft(currentValues);
      } else {
        console.log("No data to save yet");
      }
    };
    
    // Első mentés rövid késleltetéssel
    const initialSaveTimeout = setTimeout(() => {
      saveData();
    }, 3000);
    
    // Set up interval to save draft periodically
    const saveInterval = setInterval(saveData, 10000); // Save every 10 seconds
    
    return () => {
      clearTimeout(initialSaveTimeout);
      clearInterval(saveInterval);
      console.log("Cleared auto-save timer");
    };
  }, [isOpen, initialData, getValues]);
  
  // Manuálisan indítsuk el a mentést form mezők változásakor
  const watchedFields = [
    watch('companyName'), 
    watch('firstName'), 
    watch('lastName'), 
    watch('email')
  ];
  
  useEffect(() => {
    if (!isOpen || initialData) return;
    
    // Csak akkor futtassuk, ha az űrlap már nyitva van és van adat
    const currentValues = getValues();
    if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
      console.log("Field changed, triggering save");
      // Kis késleltetéssel, hogy ne minden billentyűleütésnél mentsen
      const changeTimeout = setTimeout(() => {
        saveDraft(currentValues);
      }, 1000);
      
      return () => clearTimeout(changeTimeout);
    }
  }, [watchedFields, isOpen, initialData, getValues]);

  // Save draft when modal is closed without submitting or page is unloaded
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOpen && !initialData) {
        const currentValues = getValues();
        // Don't save empty forms
        if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
          console.log("Saving draft before unload");
          saveDraft(currentValues);
          // Standard message for old browsers
          e.preventDefault();
          e.returnValue = '';
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, initialData, getValues]);

  // Formázott értékek beállítása a mezőkben
  React.useEffect(() => {
    // Biztosítsuk, hogy csak a form betöltése után futtassuk
    const formatAllConsumptionFields = () => {
      // Fogyasztási mezők formázása
      const consumptionValue = watch('consumptionData.consumption');
      const powerValue = watch('consumptionData.power');
      const contractPriceValue = watch('contractPeriod.price');
      const podConsumptionValue = watch('pod.annualConsumption');
      
      // Csak akkor formázzuk, ha már van érték
      if (consumptionValue) {
        const formattedConsumption = formatConsumptionDisplay(consumptionValue);
        const consumptionInput = document.querySelector('input[name="consumptionData.consumption"]') as HTMLInputElement;
        if (consumptionInput) consumptionInput.value = formattedConsumption;
      }
      
      if (powerValue) {
        const formattedPower = formatConsumptionDisplay(powerValue);
        const powerInput = document.querySelector('input[name="consumptionData.power"]') as HTMLInputElement;
        if (powerInput) powerInput.value = formattedPower;
      }
      
      if (contractPriceValue) {
        const formattedPrice = formatConsumptionDisplay(contractPriceValue);
        const priceInput = document.querySelector('input[name="contractPeriod.price"]') as HTMLInputElement;
        if (priceInput) priceInput.value = formattedPrice;
      }
      
      if (podConsumptionValue) {
        const formattedPodConsumption = formatConsumptionDisplay(podConsumptionValue);
        const podConsumptionInput = document.querySelector('input[name="pod.annualConsumption"]') as HTMLInputElement;
        if (podConsumptionInput) podConsumptionInput.value = formattedPodConsumption;
      }
      
      // Additional PODs formázása
      additionalPods.forEach((podIndex) => {
        const podValue = watch(`additionalPods.${podIndex}.annualConsumption`);
        if (podValue) {
          const formattedPodValue = formatConsumptionDisplay(podValue);
          const podInput = document.querySelector(`input[name="additionalPods.${podIndex}.annualConsumption"]`) as HTMLInputElement;
          if (podInput) podInput.value = formattedPodValue;
        }
      });
    };
    
    // A form betöltése után kis késleltetéssel formázzuk az értékeket
    const timeoutId = setTimeout(() => {
      formatAllConsumptionFields();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, initialData, additionalPods]); // Csak a form megnyitásakor és a initialData változásakor futtatjuk
  
  const customerType = watch('type');

  // Document upload handler
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Generáljunk egyedi fájlnevet az ütközések elkerülése érdekében
      const timestamp = Date.now();
      const uniqueFileName = `${timestamp}_${file.name}`;

      // Ideiglenes vagy valós ügyfél azonosító használata
      const customerId = initialData?.id || 'temp-' + timestamp;
      const path = `customers/${customerId}/documents/${uniqueFileName}`;

      console.log('Uploading document to path:', path);
      const fileUrl = await uploadFile(file, path);

      const currentDocuments = watch('documents');
      const newDocument = {
        id: timestamp.toString(),
        name: uniqueFileName, // Tároljuk az egyedi fájlnevet, hogy később törölni tudjuk
        originalName: file.name, // Az eredeti fájlnév megjelenítéshez
        url: fileUrl,
        uploadedAt: Timestamp.now(),
        type: 'other' // Alapértelmezett típus, amelyet a dokumentumtípus kiválasztó fogja felülírni
      };

      // Frissítsük a form állapotát
      const updatedDocuments = [...currentDocuments, newDocument];
      setValue('documents', updatedDocuments);

      // Új feltöltött dokumentum megjelenítése a felhasználói felületen és a típus kiválasztás megnyitása
      setSelectedDocumentToEdit(newDocument);

      // Ha már van mentett ügyfél, frissítsük az adatbázist is
      if (initialData?.id) {
        try {
          const customerRef = doc(db, 'customers', initialData.id);
          await updateDoc(customerRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          console.log('Document reference added to database');
        } catch (dbError) {
          console.error('Database update error:', dbError);
        }
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Delete document handler
  const handleDocumentDelete = async (documentId: string) => {
    const documents = watch('documents');
    const documentToDelete = documents.find(doc => doc.id === documentId);

    if (!documentToDelete) return;

    try {
      // Frissítsük először a form állapotát, hogy a felhasználó azonnal visszajelzést kapjon
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      setValue('documents', updatedDocuments);

      // Ha meglévő ügyfélről van szó, frissítsük az adatbázist is
      if (initialData?.id) {
        try {
          // Töröljük a dokumentum referenciát az adatbázisból
          const customerRef = doc(db, 'customers', initialData.id);
          await updateDoc(customerRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          console.log('Document reference removed from database');

          // Csak ezután próbáljuk törölni a fájlt a tárolóból
          const path = `customers/${initialData.id}/documents/${documentToDelete.name}`;
          await deleteFile(path);
          console.log('Document file deleted from storage');
        } catch (error) {
          console.error('Error updating database or deleting file:', error);
          // A hibát elnyeljük, mert a felhasználói élmény szempontjából
          // már megtörtént a dokumentum törlése a felületen
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      // Ha hiba történt a form frissítésénél, próbáljuk visszaállítani
      setValue('documents', documents);
    }
  };

  // Dokumentum típus változtatása
  const handleDocumentTypeChange = (documentId: string, type: string) => {
    const documents = watch('documents');
    const updatedDocuments = documents.map(doc =>
      doc.id === documentId ? { ...doc, type } : doc
    );

    setValue('documents', updatedDocuments);

    // Ha már van mentett ügyfél, frissítsük az adatbázist is
    if (initialData?.id) {
      try {
        const customerRef = doc(db, 'customers', initialData.id);
        updateDoc(customerRef, {
          documents: updatedDocuments,
          updatedAt: serverTimestamp()
        });
        console.log('Document type updated in database');
      } catch (error) {
        console.error('Error updating document type:', error);
      }
    }

    // Bezárjuk a dokumentum típus választó modált
    setSelectedDocumentToEdit(null);
  };

  // Checkbox handler a több érték kezeléséhez
  const handleOrganizationChange = (orgId: string, checked: boolean) => {
    const currentOrgs = watch('organizations') || [];
    const newOrgs = checked
      ? [...currentOrgs, orgId]
      : currentOrgs.filter(id => id !== orgId);
    setValue('organizations', newOrgs);
  };

  // Szűrt POD-ok
  const filteredPodIndexes = useMemo(() => {
    if (!podSearchTerm || additionalPods.length === 0) return additionalPods;
    
    const lowerSearch = podSearchTerm.toLowerCase();
    const allPodsData = watch('additionalPods') || [];
    
    return additionalPods.filter((podIndex, index) => {
      const pod = allPodsData[index];
      if (!pod) return false;
      
      return (
        (pod.id && pod.id.toLowerCase().includes(lowerSearch)) ||
        (pod.address && pod.address.toLowerCase().includes(lowerSearch)) ||
        (pod.measurementType && pod.measurementType.toLowerCase().includes(lowerSearch))
      );
    });
  }, [podSearchTerm, additionalPods, watch('additionalPods')]);

  // POD mezők hozzáadása
  const handleAddPod = () => {
    const newPods = [...additionalPods, additionalPods.length];
    setAdditionalPods(newPods);

    // Új POD adat hozzáadása a form adatokhoz
    const currentAdditionalPods = watch('additionalPods') || [];
    setValue('additionalPods', [
      ...currentAdditionalPods,
      {
        id: '',
        address: '',
        partnerId: '',
        postalCode: '',
        city: '',
        street: '',
        measurementType: '',
        annualConsumption: ''
      }
    ]);
  };

  // POD mező törlése
  const handleRemovePod = (index: number) => {
    const newPods = additionalPods.filter((_, i) => i !== index);
    setAdditionalPods(newPods);

    // A megfelelő elem törlése a form adatokból
    const currentAdditionalPods = watch('additionalPods') || [];
    const updatedPods = currentAdditionalPods.filter((_, i) => i !== index);
    setValue('additionalPods', updatedPods);
  };

  // Tömeges POD feltöltés kezelése
  const handleBulkPodUpload = (pods: any[]) => {
    // AMR/SLP -> form értékek mappolása
    const mapMeasurementType = (type: string): string => {
      const upper = (type || '').toUpperCase();
      if (upper === 'AMR') return 'negyedoras';
      if (upper === 'SLP') return 'profil';
      return type; // ha már helyes érték (negyedoras, profil, stb.)
    };

    const mappedPods = pods.map(p => ({ ...p, measurementType: mapMeasurementType(p.measurementType) }));

    const currentMainPodId = watch('pod.id');
    let podsForAdditional = mappedPods;

    // Ha a fő POD üres, az első importált POD oda kerül
    if (!currentMainPodId && mappedPods.length > 0) {
      const [firstPod, ...rest] = mappedPods;
      setValue('pod.id', firstPod.id);
      setValue('pod.address', firstPod.address);
      setValue('pod.postalCode', firstPod.postalCode || '');
      setValue('pod.city', firstPod.city || '');
      setValue('pod.street', firstPod.street || '');
      setValue('pod.measurementType', firstPod.measurementType);
      setValue('pod.annualConsumption', firstPod.annualConsumption);
      podsForAdditional = rest;
    }

    if (podsForAdditional.length > 0) {
      const currentAdditionalPods = watch('additionalPods') || [];
      const startIndex = additionalPods.length;
      const newPodIndexes = podsForAdditional.map((_, index) => startIndex + index);
      setAdditionalPods([...additionalPods, ...newPodIndexes]);
      setValue('additionalPods', [...currentAdditionalPods, ...podsForAdditional]);
    }
    
    toast.success(`${pods.length} POD sikeresen hozzáadva!`);

    // Automatikusan szétbontjuk az aggregált címeket
    handleSplitPodAddresses();
  };

  // POD címek szétbontása aggregált address mezőből
  const handleSplitPodAddresses = () => {
    let totalSplit = 0;

    // Fő POD
    const mainAddress = watch('pod.address');
    const mainPostal = watch('pod.postalCode');
    const mainCity = watch('pod.city');
    const mainStreet = watch('pod.street');
    if (mainAddress && (!mainPostal || !mainCity || !mainStreet)) {
      const match = mainAddress.match(/^(\d{4})\s+(.+?),\s*(.+)$/);
      if (match) {
        if (!mainPostal) setValue('pod.postalCode', match[1], { shouldDirty: true });
        if (!mainCity) setValue('pod.city', match[2], { shouldDirty: true });
        if (!mainStreet) setValue('pod.street', match[3], { shouldDirty: true });
        totalSplit++;
      }
    }

    // Additional PODs
    const additional = watch('additionalPods') || [];
    additional.forEach((pod, idx) => {
      if (pod.address && (!pod.postalCode || !pod.city || !pod.street)) {
        const match = pod.address.match(/^(\d{4})\s+(.+?),\s*(.+)$/);
        if (match) {
          if (!pod.postalCode) setValue(`additionalPods.${idx}.postalCode`, match[1], { shouldDirty: true });
          if (!pod.city) setValue(`additionalPods.${idx}.city`, match[2], { shouldDirty: true });
          if (!pod.street) setValue(`additionalPods.${idx}.street`, match[3], { shouldDirty: true });
          totalSplit++;
        }
      }
    });

    if (totalSplit > 0) {
      toast.success(`${totalSplit} POD cím szétbontva!`);
    } else {
      toast('Nincs szétbontható cím (formátum: 1234 Város, Utca 1.)');
    }
  };

  const handleDocumentClick = (e: React.MouseEvent<HTMLButtonElement>, document: any) => {
    e.preventDefault();
    e.stopPropagation();
    // Átalakítjuk a dokumentumot a megfelelő formátumra
    const previewDoc = {
      ...document,
      fileUrl: document.url,
      name: document.originalName || document.name
    };
    setPreviewDocument(previewDoc);
    setIsPreviewOpen(true);
  };

  // Handle tax number formatting
  const handleTaxNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatTaxNumber(e.target.value);
    setValue('taxNumber', formattedValue);
  };

  // Handle registration number formatting
  const handleRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatRegistrationNumber(e.target.value);
    setValue('registrationNumber', formattedValue);
  };

  // Fogyasztás mező formázása ezres tagolással
  const formatConsumptionDisplay = (value: string | number): string => {
    if (!value) return '';
    
    // Ha szám típusú, alakítsuk át stringgé
    const stringValue = value.toString();
    
    // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
    const cleanValue = stringValue.replace(/[^0-9.]/g, '');
    
    // Szétválasztjuk a tizedes rész előtti és utáni részre
    const parts = cleanValue.split('.');
    
    // Formázzuk az egész részt ezres tagolással
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    // Összerakjuk az eredményt
    return parts.length > 1 ? parts.join('.') : parts[0];
  };

  // Form submission
  const onSubmitHandler = async (data: CustomerFormData) => {
    try {
      // Készítünk egy másolatot az adatokról, hogy ne módosítsuk közvetlenül a RHF adatait
      const submissionData = { ...data };

      // Ensure paymentDue has the proper format (just the number, "nap" suffix is handled by mapper)
      if (submissionData.consumptionData?.dueDate) {
        submissionData.consumptionData.dueDate = submissionData.consumptionData.dueDate.toString().replace(/ nap$/, '');
      }

      // Biztosítsuk, hogy minden fogyasztás-érték csak számokat tartalmazzon
      if (submissionData.consumptionData?.consumption) {
        submissionData.consumptionData.consumption = submissionData.consumptionData.consumption.toString().replace(/\s/g, '');
      }
      
      if (submissionData.consumptionData?.power) {
        submissionData.consumptionData.power = submissionData.consumptionData.power.toString().replace(/\s/g, '');
      }
      
      if (submissionData.contractPeriod?.price) {
        submissionData.contractPeriod.price = submissionData.contractPeriod.price.toString().replace(/\s/g, '');
      }
      
      if (submissionData.pod?.annualConsumption) {
        submissionData.pod.annualConsumption = submissionData.pod.annualConsumption.toString().replace(/\s/g, '');
      }
      
      // Additional PODs numerikus értékének biztosítása
      if (submissionData.additionalPods && submissionData.additionalPods.length > 0) {
        submissionData.additionalPods = submissionData.additionalPods.map(pod => ({
          ...pod,
          annualConsumption: pod.annualConsumption ? pod.annualConsumption.toString().replace(/\s/g, '') : ''
        }));
      }

      // Szakreferens hozzáadása ha ki van választva
      const dataWithConsultant: any = {
        ...submissionData
      };
      
      if (selectedConsultantId) {
        const selectedConsultant = consultants.find(c => c.id === selectedConsultantId);
        if (selectedConsultant) {
          dataWithConsultant.consultant = {
            id: selectedConsultant.id,
            companyName: selectedConsultant.companyName
          };
        }
      }

      // Törli a piszkozatot, mivel a form beküldésre került
      console.log("Form submitted, clearing draft");
      setHasDraft(false);
      setDraftTimestamp(null);
      
      // Törli a localStorage-ból a piszkozatot
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
        console.log("Draft cleared from localStorage after submit");
      } catch (clearError) {
        console.error("Error clearing draft:", clearError);
      }

      onSubmit(dataWithConsultant as CreateCustomerData);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('common.errors.somethingWentWrong'));
    }
  };

  // Modal bezárás kezelése
  const handleModalClose = () => {
    // Ha új ügyfelet rögzít és van értelmes adat, mentse el piszkozatként
    if (!initialData) {
      const currentValues = getValues();
      console.log("Modal close, current values:", currentValues);
      if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
        console.log("Saving draft on modal close");
        saveDraft(currentValues);
        toast.success('A vázlat elmentve');
      }
    }
    
    onCancel();
  };

  // Debug funkció a localStorage ellenőrzéséhez
  useEffect(() => {
    if (isOpen) {
      // Közvetlen hozzáférés a localStorage-hoz
      try {
        // Tesztadat tárolása
        localStorage.setItem('customer_form_test', 'test_value');
        console.log('Test localStorage write successful');
        
        // Tesztadat olvasása
        const testValue = localStorage.getItem('customer_form_test');
        console.log('Test localStorage read result:', testValue);
        
        // Tesztadat törlése
        localStorage.removeItem('customer_form_test');
        console.log('Test localStorage delete successful');
        
        // Aktuális piszkozat ellenőrzése
        const currentDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        const currentTimestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
        console.log('Current draft in storage:', currentDraft ? 'exists' : 'does not exist');
        console.log('Current timestamp in storage:', currentTimestamp);
      } catch (error) {
        console.error('Error accessing localStorage:', error);
      }
    }
  }, [isOpen]);

  // Automatikus customerCode generálás új ügyfél esetén vagy ha nincs még kód
  useEffect(() => {
    if (isOpen && (!initialData || !initialData.customerCode)) {
      // Új ügyfél esetén vagy ha nincs még customerCode
      const generatedCode = generateCustomerCode(customers);
      setValue('customerCode', generatedCode);
      console.log('Generated customer code:', generatedCode, 'for', initialData ? 'existing customer without code' : 'new customer');
    }
  }, [isOpen, initialData, customers, setValue]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={initialData ? t('customers.form.title.edit') : t('customers.form.title.new')}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6 p-6">
        {/* Draft Recovery Banner */}
        <DraftRecoveryBanner />
        
        {/* Customer Type Section */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <IdentificationIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">{t('customers.form.type.title')}</h3>
              
              {/* Meglévő kapcsolat jelölőnégyzet - csak új ügyfél esetén jelenjen meg */}
              {!initialData && (
                <label className="relative flex items-center group mb-4">
                  <input
                    type="checkbox"
                    checked={isExistingRelationship}
                    onChange={(e) => setIsExistingRelationship(e.target.checked)}
                    className="sr-only peer opacity-0 absolute"
                  />
                  <div className="flex items-center px-4 py-2.5 rounded-lg border border-gray-300 
                                peer-checked:border-green-600 peer-checked:bg-green-100
                                hover:border-green-300 hover:bg-green-50 cursor-pointer transition-all duration-200
                                shadow-sm peer-checked:shadow">
                    <div className="h-5 w-5 mr-3 rounded border border-green-400 
                                  flex items-center justify-center
                                  peer-checked:bg-green-600 peer-checked:border-green-600
                                  peer-checked:shadow-inner">
                      {isExistingRelationship && (
                        <svg className="h-3.5 w-3.5 text-yellow-300" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-gray-700 peer-checked:text-green-800 font-medium">
                      {t('customers.form.existingRelationship')}
                    </span>
                  </div>
                </label>
              )}
              
              <div className="flex gap-2">
                <label className="relative flex items-center group flex-1">
                  <input
                    type="radio"
                    value="individual"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 
                                peer-checked:border-primary-500 peer-checked:bg-primary-50
                                hover:border-gray-400 cursor-pointer transition-all duration-200">
                    <UserIcon className="w-4 h-4 mr-2 text-gray-600 peer-checked:text-primary-600" />
                    <span className="text-sm text-gray-700 peer-checked:text-primary-700">
                      {t('customers.form.type.individual')}
                    </span>
                  </div>
                </label>
                <label className="relative flex items-center group flex-1">
                  <input
                    type="radio"
                    value="organization"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 
                                peer-checked:border-primary-500 peer-checked:bg-primary-50
                                hover:border-gray-400 cursor-pointer transition-all duration-200">
                    <BuildingOffice2Icon className="w-4 h-4 mr-2 text-gray-600 peer-checked:text-primary-600" />
                    <span className="text-sm text-gray-700 peer-checked:text-primary-700">
                      {t('customers.form.type.organization')}
                    </span>
                  </div>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-800 mb-4">
                  {t('customers.form.organizations.title')}
                </h3>
                <div className="space-y-4">
                  {/* Organizations checkboxes */}
                  <div className="flex flex-wrap gap-2">
                    {organizations.map((org) => {
                      const isChecked = watch('organizations')?.includes(org.id) || false;
                      return (
                        <label
                          key={org.id}
                          className="relative flex items-center group"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="flex items-center px-3 py-2 rounded-lg border border-gray-300 
                                        peer-checked:border-primary-500 peer-checked:bg-primary-50
                                        hover:border-gray-400 cursor-pointer transition-all duration-200">
                            <BuildingOffice2Icon className="w-4 h-4 mr-2 text-gray-600 peer-checked:text-primary-600" />
                            <span className="text-sm text-gray-700 peer-checked:text-primary-700">
                              {org.name}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Notes field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.organizations.notes')}
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={3}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('customers.form.organizations.notesPlaceholder')}
                    />
                  </div>

                  {/* Consultant field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Szakreferens
                    </label>
                    <select
                      value={selectedConsultantId}
                      onChange={(e) => setSelectedConsultantId(e.target.value)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">Nincs szakreferens</option>
                      {consultants.map((consultant) => (
                        <option key={consultant.id} value={consultant.id}>
                          {consultant.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Gáz ügyfél checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('isGasCustomer')}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">🔥 {t('gas.customer.isGasCustomer')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Szerződéses adatok */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                {t('customers.form.contractDetails.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.customerCode')}
                  </label>
                  <input
                    type="text"
                    {...register('customerCode')}
                    placeholder="0001"
                    maxLength={4}
                    readOnly={!initialData || !!initialData.customerCode} // Csak olvasható ha új ügyfél VAGY már van customerCode
                    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 ${
                               (!initialData || !!initialData.customerCode) 
                                 ? 'bg-gray-100 cursor-not-allowed' 
                                 : 'bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
                             }`}
                    onChange={(e) => {
                      // Csak számokat engedünk és csak ha szerkeszthető
                      if (initialData && !initialData.customerCode) {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setValue('customerCode', value);
                      }
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {!initialData 
                      ? 'Automatikusan generált egyedi azonosító'
                      : initialData.customerCode
                        ? 'Védett ügyfélazonosító - nem módosítható'
                        : 'Generált azonosító - szükség esetén módosítható'
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.contractNumber')}
                  </label>
                  <input
                    type="text"
                    {...register('contractNumber')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.companyName')}
                  </label>
                  <input
                    type="text"
                    {...register('companyName')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.companyAddress')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      {...register('companyAddress')}
                      className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const companyAddress = watch('companyAddress');
                        if (!companyAddress) return;
                        const match = companyAddress.match(/^(\d{4})\s+(.+?),\s*(.+)$/);
                        if (match) {
                          setValue('address.postalCode', match[1]);
                          setValue('address.city', match[2]);
                          setValue('address.street', match[3]);
                          toast.success('Cím szétszedve külön mezőkre – kattints a Mentés gombra!');
                        } else {
                          toast.error('Nem sikerült szétszedni a címet. Formátum: 1234 Város, Utca 1');
                        }
                      }}
                      className="px-3 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors whitespace-nowrap"
                      title="Cím szétszedése irányítószám, város, utca mezőkre"
                    >
                      Szétszedés ↓
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Formátum: 1234 Város, Utca házszám
                  </p>
                </div>

                {/* Külön címadatok számlázáshoz */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.address.postalCode')}
                    </label>
                    <input
                      type="text"
                      {...register('address.postalCode')}
                      placeholder="4700"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.address.city')}
                    </label>
                    <input
                      type="text"
                      {...register('address.city')}
                      placeholder="Mátészalka"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.address.street')}
                    </label>
                    <input
                      type="text"
                      {...register('address.street')}
                      placeholder="Babits M. út 14"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.taxNumber')}
                  </label>
                  <input
                    type="text"
                    value={watch('taxNumber') || ''}
                    onChange={handleTaxNumberChange}
                    placeholder="12345678-1-23"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Közösségi adószám (EU VAT)
                  </label>
                  <input
                    type="text"
                    {...register('euVatNumber')}
                    placeholder="HU12345678"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.registrationNumber')}
                  </label>
                  <input
                    type="text"
                    value={watch('registrationNumber') || ''}
                    onChange={handleRegistrationNumberChange}
                    placeholder="01-10-123456"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractDetails.bankAccount')}
                  </label>
                  <input
                    type="text"
                    {...register('bankAccount')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Képviselő és kapcsolattartó adatok */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <UserIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">{t('customers.form.contactDetails.title')}</h3>

              {/* Képviselő */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Képviselő</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.contactDetails.representativeName')}
                    </label>
                    <input
                      type="text"
                      {...register('representativeName')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Képviselő telefonszáma
                    </label>
                    <input
                      type="tel"
                      {...register('representativePhone')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Képviselő email címe
                    </label>
                    <input
                      type="email"
                      {...register('representativeEmail')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Kapcsolattartó */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kapcsolattartó</p>
                  <button
                    type="button"
                    onClick={() => {
                      setValue('contactPerson.name', watch('representativeName') || '');
                      setValue('contactPerson.phone', watch('representativePhone') || '');
                      setValue('contactPerson.email', watch('representativeEmail') || '');
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Képviselő = Kapcsolattartó
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.contactDetails.contactName')}
                    </label>
                    <input
                      type="text"
                      {...register('contactPerson.name')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.contactDetails.contactPhone')}
                    </label>
                    <input
                      type="tel"
                      {...register('contactPerson.phone')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.contactDetails.contactEmail')}
                    </label>
                    <input
                      type="email"
                      {...register('contactPerson.email')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pénzügyi kapcsolattartó */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-6">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <UserIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 mb-4">{t('customers.form.financialContact.title')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-amber-50 rounded-lg border-2 border-amber-300">
                  <label className="text-sm font-semibold text-amber-900 mb-2 flex items-center">
                    <EnvelopeIcon className="w-4 h-4 mr-2 text-amber-600" />
                    {t('customers.form.billingEmail')}
                  </label>
                  <input
                    type="email"
                    {...register('billingEmail')}
                    placeholder="szamlazas@ceg.hu"
                    className="w-full bg-white border-2 border-amber-400 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    📧 Számlák küldésére használt e-mail cím
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.financialContact.name')}
                  </label>
                  <input
                    type="text"
                    {...register('financialContact.name')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.financialContact.email')}
                  </label>
                  <input
                    type="email"
                    {...register('financialContact.email')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.financialContact.phone')}
                  </label>
                  <input
                    type="tel"
                    {...register('financialContact.phone')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* További számlázási e-mail címek */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('customers.form.additionalBillingEmails')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newBillingEmail}
                    onChange={(e) => setNewBillingEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddBillingEmail();
                      }
                    }}
                    placeholder="email@ceg.hu"
                    className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddBillingEmail}
                    className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                  >
                    + Hozzáadás
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(watch('additionalBillingEmails') || []).map((email, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md text-sm text-gray-700"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => {
                          const current = watch('additionalBillingEmails') || [];
                          setValue('additionalBillingEmails', current.filter((_, i) => i !== idx), { shouldDirty: true });
                        }}
                        className="text-amber-600 hover:text-red-600 ml-1"
                        title="Törlés"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fogyasztási adatok */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-6">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentChartBarIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 mb-4">{t('customers.form.consumptionData.title')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.consumptionData.date')}
                  </label>
                  <DatePicker
                    selected={watch('consumptionData.date') ? new Date(watch('consumptionData.date')) : null}
                    onChange={(date) => setValue('consumptionData.date', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.consumptionData.dueDate')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      {...register('consumptionData.dueDate')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                              placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      onBlur={(e) => {
                        // Ensure the value is properly formatted for display
                        const value = e.target.value;
                        if (value) {
                          // The "nap" suffix is only displayed visually, the actual value is numeric
                          setValue('consumptionData.dueDate', value);
                        }
                      }}
                    />
                    <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500">
                      nap
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.consumptionData.power')}
                  </label>
                  <input
                    type="text"
                    {...register('consumptionData.power')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    onChange={(e) => {
                      // Csak a számokat és a pontot engedjük meg
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setValue('consumptionData.power', value);
                    }}
                    onFocus={(e) => {
                      // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                      e.target.value = watch('consumptionData.power') || '';
                    }}
                    onBlur={(e) => {
                      // Fókusz elvesztésekor újra formázzuk
                      e.target.value = formatConsumptionDisplay(watch('consumptionData.power') || '');
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.consumptionData.profileNumber')}
                  </label>
                  <input
                    type="text"
                    {...register('consumptionData.profileNumber')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.consumptionData.consumption')}
                  </label>
                  <input
                    type="text"
                    {...register('consumptionData.consumption')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    onChange={(e) => {
                      // Csak a számokat és a pontot engedjük meg
                      const value = e.target.value.replace(/[^\d.]/g, '');
                      setValue('consumptionData.consumption', value);
                    }}
                    onBlur={(e) => {
                      // Formázott megjelenítés a mezőben
                      const rawValue = e.target.value;
                      const formattedValue = formatConsumptionDisplay(rawValue);
                      e.target.value = formattedValue;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Szerződés időtartama és ár */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-sky-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                {t('customers.form.contractPeriod.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractPeriod.startDate')}
                  </label>
                  <DatePicker
                    selected={watch('contractPeriod.startDate') ? new Date(watch('contractPeriod.startDate')) : null}
                    onChange={(date) => setValue('contractPeriod.startDate', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractPeriod.endDate')}
                  </label>
                  <DatePicker
                    selected={watch('contractPeriod.endDate') ? new Date(watch('contractPeriod.endDate')) : null}
                    onChange={(date) => setValue('contractPeriod.endDate', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ár egysége
                  </label>
                  <select
                    {...register('contractPeriod.priceUnit')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Válassz...</option>
                    <option value="HUF/kWh">HUF/kWh</option>
                    <option value="EUR/MWh">EUR/MWh</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fizetési pénznem
                  </label>
                  <select
                    {...register('contractPeriod.paymentCurrency')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Válassz...</option>
                    <option value="HUF">HUF</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.contractPeriod.price')}
                    {watch('contractPeriod.priceUnit') && (
                      <span className="ml-1 text-gray-400 font-normal">({watch('contractPeriod.priceUnit')})</span>
                    )}
                  </label>
                  <input
                    type="text"
                    {...register('contractPeriod.price')}
                    placeholder={watch('contractPeriod.priceUnit') === 'EUR/MWh' ? 'pl. 45.5' : 'pl. 28.5'}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.]/g, '');
                      setValue('contractPeriod.price', value);
                    }}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const formattedValue = formatConsumptionDisplay(rawValue);
                      e.target.value = formattedValue;
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ár típusa
                  </label>
                  <label className="flex items-center space-x-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      {...register('contractPeriod.indexedPrice')}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-900">📊 Indexált ár</span>
                  </label>
                </div>

                {watch('contractPeriod.indexedPrice') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Spread ({watch('contractPeriod.priceUnit') === 'EUR/MWh' ? '€/MWh' : 'Ft/kWh'})
                    </label>
                    <input
                      type="text"
                      {...register('contractPeriod.spread')}
                      placeholder={watch('contractPeriod.priceUnit') === 'EUR/MWh' ? 'pl. 1.5' : 'pl. 2.5'}
                      className="w-full bg-white border border-blue-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ajánlati ár és valutanem */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-rose-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                {t('customers.form.offerPrice.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.offerPrice.currency')}
                  </label>
                  <select
                    {...register('offerPrice.currency')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="HUF">HUF</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.offerPrice.price')}
                  </label>
                  <select
                    {...register('offerPrice.price')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="HUF/kWh">HUF/kWh</option>
                    <option value="EUR/MWh">EUR/MWh</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KÁT adatok */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                {t('customers.form.kat.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.kat.kat')}
                  </label>
                  <input
                    type="text"
                    {...register('kat.kat')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EKR (Energiahatékonysági díj) */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-amber-100 to-yellow-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentChartBarIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                Energiahatékonysági díj (EKR)
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setValue('ekrSetting.type', 'standard');
                    setValue('ekrSetting.customPrice', '');
                  }}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    watch('ekrSetting.type') === 'standard' || !watch('ekrSetting.type')
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900">Alap</div>
                  <div className="text-xs text-gray-500 mt-1">Az aktuális hatályos EKR díj érvényes (a tételeknél beállított ár)</div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setValue('ekrSetting.type', 'exempt');
                    setValue('ekrSetting.customPrice', '0');
                  }}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    watch('ekrSetting.type') === 'exempt'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900">Kötelezett (0 Ft)</div>
                  <div className="text-xs text-gray-500 mt-1">Az ügyfél maga is kötelezett, ezért 0 HUF/kWh</div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setValue('ekrSetting.type', 'custom');
                    setValue('ekrSetting.customPrice', '');
                  }}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    watch('ekrSetting.type') === 'custom'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900">Egyedi ár</div>
                  <div className="text-xs text-gray-500 mt-1">Egyedi megállapodás szerinti EKR díj</div>
                </button>
              </div>

              {watch('ekrSetting.type') === 'custom' && (
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Egyedi EKR ár (HUF/kWh)
                  </label>
                  <input
                    type="text"
                    {...register('ekrSetting.customPrice')}
                    placeholder="pl. 0.35"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* POD adatok */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-800">
                  POD 1
                </h3>
                <button
                  type="button"
                  onClick={handleSplitPodAddresses}
                  className="text-xs px-2.5 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                  title="Pl. 4700 Csengele, Fő utca 12. → Irányítószám / Város / Utca"
                >
                  Címek szétbontása
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    POD 1
                  </label>
                  <input
                    type="text"
                    {...register('pod.id')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.pod.address')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.address')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.pod.partnerId')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.partnerId')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {/* POD cím szétbontva */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.address.postalCode')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.postalCode')}
                    placeholder="4700"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.address.city')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.city')}
                    placeholder="Csengele"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.address.street')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.street')}
                    placeholder="Fő utca 12."
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.pod.measurementType')}
                  </label>
                  <select
                    {...register('pod.measurementType')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">Válassz mérési típust...</option>
                    <optgroup label="AMR (Negyedórás)">
                      <option value="negyedoras">AMR - Negyedórás (15 perc)</option>
                      <option value="negyedoras_hmke">AMR - Negyedórás (15 perc) - HMKE</option>
                    </optgroup>
                    <optgroup label="SLP (Profilos)">
                      <option value="profil">SLP (profil alapú)</option>
                      <option value="tavmert_slp">SLP (KIF4) - Távmért</option>
                      <option value="profil_hmke">SLP (profil alapú) - HMKE</option>
                      <option value="tavmert_slp_hmke">SLP (KIF4) - Távmért - HMKE</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.pod.annualConsumption')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.annualConsumption')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setValue('pod.annualConsumption', value);
                    }}
                    onFocus={(e) => {
                      e.target.value = watch('pod.annualConsumption') || '';
                    }}
                    onBlur={(e) => {
                      e.target.value = formatConsumptionDisplay(watch('pod.annualConsumption') || '');
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szorzó
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('pod.multiplier')}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="pl. 1, 1.5, 2"
                  />
                </div>
              </div>

              {/* HMKE / Kiserőmű adatok */}
              <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Kiserőmű (HMKE / KE)</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      setValue('pod.smallPowerPlant', { type: 'none', isReversePower: false });
                    }}
                    className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                      !watch('pod.smallPowerPlant') || watch('pod.smallPowerPlant.type') === 'none'
                        ? 'border-gray-400 bg-gray-100'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">Nincs</div>
                    <div className="text-xs text-gray-500 mt-0.5">Nincs kiserőmű a POD-on</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const current = watch('pod.smallPowerPlant');
                      setValue('pod.smallPowerPlant', { 
                        ...current, 
                        type: 'hmke', 
                        isReversePower: current?.isReversePower || false 
                      });
                    }}
                    className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                      watch('pod.smallPowerPlant.type') === 'hmke'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">HMKE</div>
                    <div className="text-xs text-gray-500 mt-0.5">Háztartási méretű kiserőmű</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const current = watch('pod.smallPowerPlant');
                      setValue('pod.smallPowerPlant', { 
                        ...current, 
                        type: 'ke', 
                        isReversePower: current?.isReversePower || false 
                      });
                    }}
                    className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                      watch('pod.smallPowerPlant.type') === 'ke'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">KE</div>
                    <div className="text-xs text-gray-500 mt-0.5">Kiserőmű (50 MW alatt)</div>
                  </button>
                </div>

                {watch('pod.smallPowerPlant.type') && watch('pod.smallPowerPlant.type') !== 'none' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={watch('pod.smallPowerPlant.isReversePower') || false}
                          onChange={(e) => {
                            const current = watch('pod.smallPowerPlant');
                            setValue('pod.smallPowerPlant', { 
                              type: current?.type || 'none', 
                              isReversePower: e.target.checked,
                              installationDate: current?.installationDate,
                              inverterCapacityKva: current?.inverterCapacityKva,
                              feedInCapacityKva: current?.feedInCapacityKva,
                              settlementType: current?.settlementType
                            });
                          }}
                          className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Visszwattos rendszer</span>
                      </label>
                      <span className="text-xs text-gray-500">(betáplálási telj. &lt; inverter telj.)</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Telepítés dátuma</label>
                        <input
                          type="date"
                          value={watch('pod.smallPowerPlant.installationDate') || ''}
                          onChange={(e) => {
                            const current = watch('pod.smallPowerPlant');
                            setValue('pod.smallPowerPlant', { 
                              type: current?.type || 'none', 
                              isReversePower: current?.isReversePower || false,
                              installationDate: e.target.value,
                              inverterCapacityKva: current?.inverterCapacityKva,
                              feedInCapacityKva: current?.feedInCapacityKva,
                              settlementType: current?.settlementType
                            });
                          }}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Inverter telj. (kVA)</label>
                        <input
                          type="text"
                          value={watch('pod.smallPowerPlant.inverterCapacityKva') || ''}
                          onChange={(e) => {
                            const current = watch('pod.smallPowerPlant');
                            setValue('pod.smallPowerPlant', { 
                              type: current?.type || 'none', 
                              isReversePower: current?.isReversePower || false,
                              installationDate: current?.installationDate,
                              inverterCapacityKva: e.target.value,
                              feedInCapacityKva: current?.feedInCapacityKva,
                              settlementType: current?.settlementType
                            });
                          }}
                          placeholder="pl. 10"
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Betáplálási telj. (kVA)</label>
                        <input
                          type="text"
                          value={watch('pod.smallPowerPlant.feedInCapacityKva') || ''}
                          onChange={(e) => {
                            const current = watch('pod.smallPowerPlant');
                            setValue('pod.smallPowerPlant', { 
                              type: current?.type || 'none', 
                              isReversePower: current?.isReversePower || false,
                              installationDate: current?.installationDate,
                              inverterCapacityKva: current?.inverterCapacityKva,
                              feedInCapacityKva: e.target.value,
                              settlementType: current?.settlementType
                            });
                          }}
                          placeholder="pl. 0"
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Elszámolás típusa</label>
                        <select
                          value={watch('pod.smallPowerPlant.settlementType') || 'pending'}
                          onChange={(e) => {
                            const current = watch('pod.smallPowerPlant');
                            setValue('pod.smallPowerPlant', { 
                              type: current?.type || 'none', 
                              isReversePower: current?.isReversePower || false,
                              installationDate: current?.installationDate,
                              inverterCapacityKva: current?.inverterCapacityKva,
                              feedInCapacityKva: current?.feedInCapacityKva,
                              settlementType: e.target.value as 'annual_balance' | 'gross' | 'pending'
                            });
                          }}
                          className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="pending">Még nem eldöntött</option>
                          <option value="annual_balance">Éves szaldó</option>
                          <option value="gross">Bruttó elszámolás</option>
                        </select>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 bg-white p-2 rounded border border-gray-200">
                      <strong>Tájékoztató:</strong> 2023.09.07-ig jelzett igénybejelentés + 2026.01.01-ig befejezett beruházás = éves szaldó. 
                      2023.09.08-tól beérkezett igénybejelentés = bruttó elszámolás.
                    </div>
                  </div>
                )}
              </div>

              {/* Számlázási kontroll */}
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Számlázási kontroll</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pod-billable"
                      {...register('pod.billable')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="pod-billable" className="ml-2 text-sm text-gray-700 cursor-pointer">
                      Számlázható
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Felfüggesztés oka
                    </label>
                    <select
                      {...register('pod.suspensionReason')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">Nincs felfüggesztve</option>
                      <option value="reklamacio">Reklamáció</option>
                      <option value="atiras">Átírás</option>
                      <option value="ugyfel_tavozas">Ügyfél távozás</option>
                      <option value="egyeb">Egyéb</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Felfüggesztés kezdete
                    </label>
                    <input
                      type="date"
                      {...register('pod.billingSuspendedFrom')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Felfüggesztés vége
                    </label>
                    <input
                      type="date"
                      {...register('pod.billingSuspendedTo')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Mérőóra kezelés */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Mérőóra adatok</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gyári szám
                    </label>
                    <input
                      type="text"
                      {...register('pod.meterSerialNumber')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="pl. ABC123456"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mérőóra típus
                    </label>
                    <input
                      type="text"
                      {...register('pod.meterType')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="pl. Digitális, Analóg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Szerelés dátuma
                    </label>
                    <input
                      type="date"
                      {...register('pod.meterInstallationDate')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Leszerelés dátuma
                    </label>
                    <input
                      type="date"
                      {...register('pod.meterRemovalDate')}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('pod.isSlpConsumer')}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-gray-700">SLP fogyasztó (Standard Load Profile)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* További POD adatok */}
        {additionalPods.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl border-2 border-indigo-200 shadow-md">
            <div className="flex items-center mb-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-indigo-600 mr-2" />
              <h4 className="text-sm font-semibold text-indigo-900">POD Szűrő</h4>
            </div>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-indigo-400" />
              <input
                type="text"
                value={podSearchTerm}
                onChange={(e) => setPodSearchTerm(e.target.value)}
                placeholder="Keresés POD ID, cím vagy mérés típusa alapján..."
                className="w-full pl-10 pr-10 py-3 border-2 border-indigo-300 rounded-lg 
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 
                         bg-white shadow-sm text-gray-900 placeholder-indigo-300
                         transition-all duration-200"
              />
              {podSearchTerm && (
                <button
                  type="button"
                  onClick={() => setPodSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 
                           rounded-full p-1 transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
            {podSearchTerm && (
              <div className="mt-3 flex items-center justify-between p-2 bg-white rounded-lg border border-indigo-200">
                <p className="text-sm font-medium text-indigo-700">
                  🔍 {filteredPodIndexes.length} találat {additionalPods.length} POD közül
                </p>
                {filteredPodIndexes.length === 0 && (
                  <span className="text-xs text-amber-600 font-medium">Nincs találat</span>
                )}
              </div>
            )}
          </div>
        )}
        
        {filteredPodIndexes.map((podIndex, index) => (
          <div key={`additional-pod-${podIndex}`} className="bg-white shadow-md rounded-lg border border-gray-200 mb-4">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-800">
                    {t('customers.form.additionalPods.title', { index: index + 2 })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => handleRemovePod(podIndex)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.pod', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.id`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.address', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.address`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.partnerId', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.partnerId`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.postalCode', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.postalCode`)}
                      placeholder="4700"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.city', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.city`)}
                      placeholder="Csengele"
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.street', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.street`)}
                      placeholder="Fő utca 12."
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.measurementType', { index: index + 2 })}
                    </label>
                    <select
                      {...register(`additionalPods.${podIndex}.measurementType`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">Válassz mérési típust...</option>
                      <optgroup label="AMR (Negyedórás)">
                        <option value="negyedoras">AMR - Negyedórás (15 perc)</option>
                        <option value="negyedoras_hmke">AMR - Negyedórás (15 perc) - HMKE</option>
                      </optgroup>
                      <optgroup label="SLP (Profilos)">
                        <option value="profil">SLP (profil alapú)</option>
                        <option value="tavmert_slp">SLP (KIF4) - Távmért</option>
                        <option value="profil_hmke">SLP (profil alapú) - HMKE</option>
                        <option value="tavmert_slp_hmke">SLP (KIF4) - Távmért - HMKE</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('customers.form.additionalPods.annualConsumption', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.annualConsumption`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      onChange={(e) => {
                        // Csak a számokat és a pontot engedjük meg
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setValue(`additionalPods.${podIndex}.annualConsumption`, value);
                      }}
                      onFocus={(e) => {
                        // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                        e.target.value = watch(`additionalPods.${podIndex}.annualConsumption`) || '';
                      }}
                      onBlur={(e) => {
                        // Fókusz elvesztésekor újra formázzuk
                        e.target.value = formatConsumptionDisplay(watch(`additionalPods.${podIndex}.annualConsumption`) || '');
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Szorzó
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register(`additionalPods.${podIndex}.multiplier`)}
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder="pl. 1, 1.5, 2"
                    />
                  </div>
                </div>

                {/* HMKE / Kiserőmű adatok */}
                <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="text-sm font-medium text-gray-800 mb-3">Kiserőmű (HMKE / KE)</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setValue(`additionalPods.${podIndex}.smallPowerPlant`, { type: 'none', isReversePower: false });
                      }}
                      className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                        !watch(`additionalPods.${podIndex}.smallPowerPlant`) || watch(`additionalPods.${podIndex}.smallPowerPlant.type`) === 'none'
                          ? 'border-gray-400 bg-gray-100'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">Nincs</div>
                      <div className="text-xs text-gray-500 mt-0.5">Nincs kiserőmű</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                        setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                          type: 'hmke', 
                          isReversePower: current?.isReversePower || false,
                          installationDate: current?.installationDate,
                          inverterCapacityKva: current?.inverterCapacityKva,
                          feedInCapacityKva: current?.feedInCapacityKva,
                          settlementType: current?.settlementType
                        });
                      }}
                      className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                        watch(`additionalPods.${podIndex}.smallPowerPlant.type`) === 'hmke'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">HMKE</div>
                      <div className="text-xs text-gray-500 mt-0.5">Háztartási méretű kiserőmű</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                        setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                          type: 'ke', 
                          isReversePower: current?.isReversePower || false,
                          installationDate: current?.installationDate,
                          inverterCapacityKva: current?.inverterCapacityKva,
                          feedInCapacityKva: current?.feedInCapacityKva,
                          settlementType: current?.settlementType
                        });
                      }}
                      className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                        watch(`additionalPods.${podIndex}.smallPowerPlant.type`) === 'ke'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">KE</div>
                      <div className="text-xs text-gray-500 mt-0.5">Kiserőmű (50 MW alatt)</div>
                    </button>
                  </div>

                  {watch(`additionalPods.${podIndex}.smallPowerPlant.type`) && watch(`additionalPods.${podIndex}.smallPowerPlant.type`) !== 'none' && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={watch(`additionalPods.${podIndex}.smallPowerPlant.isReversePower`) || false}
                            onChange={(e) => {
                              const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                              setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                                type: current?.type || 'none', 
                                isReversePower: e.target.checked,
                                installationDate: current?.installationDate,
                                inverterCapacityKva: current?.inverterCapacityKva,
                                feedInCapacityKva: current?.feedInCapacityKva,
                                settlementType: current?.settlementType
                              });
                            }}
                            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Visszwattos rendszer</span>
                        </label>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Telepítés dátuma</label>
                          <input
                            type="date"
                            value={watch(`additionalPods.${podIndex}.smallPowerPlant.installationDate`) || ''}
                            onChange={(e) => {
                              const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                              setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                                type: current?.type || 'none', 
                                isReversePower: current?.isReversePower || false,
                                installationDate: e.target.value,
                                inverterCapacityKva: current?.inverterCapacityKva,
                                feedInCapacityKva: current?.feedInCapacityKva,
                                settlementType: current?.settlementType
                              });
                            }}
                            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Inverter telj. (kVA)</label>
                          <input
                            type="text"
                            value={watch(`additionalPods.${podIndex}.smallPowerPlant.inverterCapacityKva`) || ''}
                            onChange={(e) => {
                              const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                              setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                                type: current?.type || 'none', 
                                isReversePower: current?.isReversePower || false,
                                installationDate: current?.installationDate,
                                inverterCapacityKva: e.target.value,
                                feedInCapacityKva: current?.feedInCapacityKva,
                                settlementType: current?.settlementType
                              });
                            }}
                            placeholder="pl. 10"
                            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Betáplálási telj. (kVA)</label>
                          <input
                            type="text"
                            value={watch(`additionalPods.${podIndex}.smallPowerPlant.feedInCapacityKva`) || ''}
                            onChange={(e) => {
                              const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                              setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                                type: current?.type || 'none', 
                                isReversePower: current?.isReversePower || false,
                                installationDate: current?.installationDate,
                                inverterCapacityKva: current?.inverterCapacityKva,
                                feedInCapacityKva: e.target.value,
                                settlementType: current?.settlementType
                              });
                            }}
                            placeholder="pl. 0"
                            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Elszámolás típusa</label>
                          <select
                            value={watch(`additionalPods.${podIndex}.smallPowerPlant.settlementType`) || 'pending'}
                            onChange={(e) => {
                              const current = watch(`additionalPods.${podIndex}.smallPowerPlant`);
                              setValue(`additionalPods.${podIndex}.smallPowerPlant`, { 
                                type: current?.type || 'none', 
                                isReversePower: current?.isReversePower || false,
                                installationDate: current?.installationDate,
                                inverterCapacityKva: current?.inverterCapacityKva,
                                feedInCapacityKva: current?.feedInCapacityKva,
                                settlementType: e.target.value as 'annual_balance' | 'gross' | 'pending'
                              });
                            }}
                            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="pending">Még nem eldöntött</option>
                            <option value="annual_balance">Éves szaldó</option>
                            <option value="gross">Bruttó elszámolás</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Számlázási kontroll */}
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-medium text-gray-800 mb-3">Számlázási kontroll</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`additionalPods-${podIndex}-billable`}
                        {...register(`additionalPods.${podIndex}.billable`)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor={`additionalPods-${podIndex}-billable`} className="ml-2 text-sm text-gray-700 cursor-pointer">
                        Számlázható
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Felfüggesztés oka
                      </label>
                      <select
                        {...register(`additionalPods.${podIndex}.suspensionReason`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="">Nincs felfüggesztve</option>
                        <option value="reklamacio">Reklamáció</option>
                        <option value="atiras">Átírás</option>
                        <option value="ugyfel_tavozas">Ügyfél távozás</option>
                        <option value="egyeb">Egyéb</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Felfüggesztés kezdete
                      </label>
                      <input
                        type="date"
                        {...register(`additionalPods.${podIndex}.billingSuspendedFrom`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Felfüggesztés vége
                      </label>
                      <input
                        type="date"
                        {...register(`additionalPods.${podIndex}.billingSuspendedTo`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Mérőóra kezelés */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-gray-800 mb-3">Mérőóra adatok</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gyári szám
                      </label>
                      <input
                        type="text"
                        {...register(`additionalPods.${podIndex}.meterSerialNumber`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="pl. ABC123456"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mérőóra típus
                      </label>
                      <input
                        type="text"
                        {...register(`additionalPods.${podIndex}.meterType`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="pl. Digitális, Analóg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Szerelés dátuma
                      </label>
                      <input
                        type="date"
                        {...register(`additionalPods.${podIndex}.meterInstallationDate`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Leszerelés dátuma
                      </label>
                      <input
                        type="date"
                        {...register(`additionalPods.${podIndex}.meterRemovalDate`)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 
                                 placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register(`additionalPods.${podIndex}.isSlpConsumer`)}
                        className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">SLP fogyasztó (Standard Load Profile)</span>
                    </label>
                  </div>
                </div>

              </div>
            </div>
          </div>
        ))}

        {/* További POD hozzáadása gombok */}
        <div className="flex justify-center gap-3 mb-6">
          <button
            type="button"
            onClick={handleAddPod}
            className="flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            <span>{t('customers.form.addMore')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowBulkPodUploader(true)}
            className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 rounded-md text-white text-sm transition-colors"
          >
            <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
            <span>Tömeges feltöltés</span>
          </button>
        </div>

        {/* Document Upload Section */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">{t('customers.form.documents.title')}</h3>

              {/* Document Upload Input */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 
                                  rounded-md cursor-pointer transition-colors">
                  <PaperClipIcon className="w-4 h-4 text-gray-700" />
                  <span className="text-sm text-gray-700">
                    {t('customers.form.documents.uploadButton')}
                  </span>
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
                {isUploading && (
                  <span className="text-xs text-gray-600">
                    {t('customers.form.documents.uploading')}
                  </span>
                )}
              </div>

              {/* Document List */}
              <div className="space-y-2">
                {!Array.isArray(watch('documents')) || watch('documents').length === 0 ? (
                  <div className="text-sm text-gray-500 italic">
                    {t('customers.form.documents.empty')}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {watch('documents').map((document) => (
                      <li key={document.id} className="py-3 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          {document.type === 'contract' && <DocumentCheckIcon className="w-5 h-5 text-primary-600" />}
                          {document.type === 'quotation' && <DocumentChartBarIcon className="w-5 h-5 text-orange-600" />}
                          {(!document.type || document.type === 'other') && <DocumentIcon className="w-5 h-5 text-gray-600" />}
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              {document.originalName || document.name}
                            </div>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-600">
                                {formatDocumentDate(document.uploadedAt)}
                              </span>
                              <span className="mx-1.5 text-gray-400">•</span>
                              <button
                                onClick={(e) => handleDocumentClick(e, document)}
                                className="text-primary-600 hover:text-primary-700"
                              >
                                {document.type === 'contract' && (t('customers.form.documents.typeContract') || 'Contract')}
                                {document.type === 'quotation' && (t('customers.form.documents.typeQuotation') || 'Quotation')}
                                {(!document.type || document.type === 'other') && (t('customers.form.documents.typeOther') || 'Other')}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Tooltip content={t('customers.form.documents.view')}>
                            <button
                              onClick={(e) => handleDocumentClick(e, document)}
                              className="text-primary-600 hover:text-primary-700 transition-colors"
                            >
                              <PaperClipIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>

                          <Tooltip content={t('customers.form.documents.delete')}>
                            <button
                              type="button"
                              onClick={() => handleDocumentDelete(document.id)}
                              className="text-gray-600 hover:text-red-600 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Invoicing Panel - csak meglévő ügyfelekhez, akiknek van POD-ja */}
        {initialData && (watch('pod.id') || (watch('additionalPods') && Array.isArray(watch('additionalPods')) && watch('additionalPods')!.length > 0)) && (
          <BulkInvoicingPanel 
            customer={{
              ...initialData,
              invoiceGroups: watch('invoiceGroups') || initialData.invoiceGroups,
              bulkInvoicingSettings: watch('bulkInvoicingSettings') || initialData.bulkInvoicingSettings,
              balanceGroup: watch('balanceGroup') || initialData.balanceGroup
            }}
            onUpdate={(updates) => {
              console.log('📥 CustomerForm: BulkInvoicingPanel onUpdate called', { updates });
              // Only update form values, don't save to database
              if (updates.invoiceGroups !== undefined) {
                console.log('📝 CustomerForm: Updating invoiceGroups', { invoiceGroups: updates.invoiceGroups });
                setValue('invoiceGroups', updates.invoiceGroups, { shouldValidate: false, shouldDirty: false });
              }
              if (updates.bulkInvoicingSettings !== undefined) {
                console.log('📝 CustomerForm: Updating bulkInvoicingSettings', { bulkInvoicingSettings: updates.bulkInvoicingSettings });
                setValue('bulkInvoicingSettings', updates.bulkInvoicingSettings, { shouldValidate: false, shouldDirty: false });
              }
              if (updates.balanceGroup !== undefined) {
                console.log('📝 CustomerForm: Updating balanceGroup', { balanceGroup: updates.balanceGroup });
                setValue('balanceGroup', updates.balanceGroup, { shouldValidate: false, shouldDirty: false });
              }
              console.log('✅ CustomerForm: onUpdate completed, form values updated');
              return Promise.resolve();
            }}
          />
        )}

        {/* Földgáz POD-ok szekció - csak meglévő ügyfelekhez */}
        {initialData?.id && (
          <GasPodsSection customerId={initialData.id} />
        )}

        {/* Földgáz szerződések szekció - csak meglévő ügyfelekhez */}
        {initialData?.id && (
          <GasContractsSection customerId={initialData.id} />
        )}

        {/* Gáz mérőállások szekció - csak meglévő ügyfelekhez */}
        {initialData?.id && (
          <GasMeterReadingsSection customerId={initialData.id} />
        )}

        {/* Gáz számlák szekció - csak meglévő ügyfelekhez */}
        {initialData?.id && (
          <GasInvoicesSection customerId={initialData.id} />
        )}

        {/* Gáz nominálások szekció - csak meglévő ügyfelekhez */}
        {initialData?.id && (
          <GasNominationsSection customerId={initialData.id} />
        )}

        {/* Pricing Management Section - csak meglévő ügyfelekhez */}
        {initialData && (
          <div className="bg-white shadow-md rounded-lg border border-gray-200">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <DocumentTextIcon className="w-5 h-5 text-green-600" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-800">💰 Árazási Információk</h3>
                  
                  {/* Indexált árazást használ checkbox */}
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('bulkInvoicingSettings.indexedPricing')}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">📊 Indexált árazást használ</span>
                  </label>
                </div>

                {/* Jelenlegi árazás */}
                {watch('currentPricing') && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-green-800 mb-2">
                      📅 Jelenlegi Árazás ({watch('currentPricing')?.year})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="font-medium text-green-700">Ár:</span> {watch('currentPricing')?.price} {watch('currentPricing')?.currency}
                      </div>
                      <div>
                        <span className="font-medium text-green-700">Érvényesség:</span> {watch('currentPricing')?.validFrom} - {watch('currentPricing')?.validTo}
                      </div>
                      <div>
                        <span className="font-medium text-green-700">Státusz:</span> 
                        <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {watch('currentPricing')?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Jövőbeli árazások */}
                {watch('futurePricing') && Array.isArray(watch('futurePricing')) && watch('futurePricing')!.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                      🔮 Jövőbeli Árazások ({watch('futurePricing')!.length} db)
                    </h4>
                    <div className="space-y-2">
                      {watch('futurePricing')!.map((pricing, index) => (
                        <div key={index} className="bg-white border border-blue-200 rounded p-2">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                            <div>
                              <span className="font-medium text-blue-700">Év:</span> {pricing.year}
                            </div>
                            <div>
                              <span className="font-medium text-blue-700">Ár:</span> {pricing.price} {pricing.currency}
                            </div>
                            <div>
                              <span className="font-medium text-blue-700">Érvényesség:</span> {pricing.validFrom} - {pricing.validTo}
                            </div>
                            <div>
                              <span className="font-medium text-blue-700">Státusz:</span> 
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {pricing.status}
                              </span>
                            </div>
                          </div>
                          {pricing.consumption_kwh && (
                            <div className="mt-1 text-xs text-gray-600">
                              Fogyasztás: {pricing.consumption_kwh} kWh
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ha van hasFuturePricing flag */}
                {watch('hasFuturePricing') && (
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      🔮 Jövőbeli árazás elérhető
                    </span>
                  </div>
                )}

                {/* Ha nincs árazási adat */}
                {!watch('currentPricing') && !watch('futurePricing') && !watch('hasFuturePricing') && (
                  <div className="text-sm text-gray-500 italic">
                    Nincs strukturált árazási információ ehhez az ügyfélhez.
                  </div>
                )}

                <div className="mt-3 text-xs text-gray-500">
                  💡 Az árazási adatok Python scriptekkel kezelhetők. Az űrlapon keresztül csak megtekintés lehetséges.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 hover:text-gray-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            {initialData ? t('common.save') : t('common.create')}
          </button>
        </div>
      </form>

      {/* Dokumentum típus választó modal */}
      <Modal
        isOpen={!!selectedDocumentToEdit}
        onClose={() => setSelectedDocumentToEdit(null)}
        title={t('customers.form.documents.selectType') || 'Select Document Type'}
        size="md"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">
            {t('customers.form.documents.selectTypeDescription') || 'Please select the type of this document:'}
          </p>

          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'contract')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentCheckIcon className="w-5 h-5 mr-3 text-primary-600" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeContract') || 'Contract'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'contract' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'quotation')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentChartBarIcon className="w-5 h-5 mr-3 text-orange-600" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeQuotation') || 'Quotation'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'quotation' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'other')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-3 text-gray-600" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeOther') || 'Other Document'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'other' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setSelectedDocumentToEdit(null)}
              className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dokumentum előnézet modal */}
      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewDocument(null);
        }}
        contract={previewDocument}
      />

      {/* Tömeges POD feltöltő modal */}
      {showBulkPodUploader && (
        <BulkPodUploader
          onUpload={handleBulkPodUpload}
          onClose={() => setShowBulkPodUploader(false)}
        />
      )}
    </Modal>
  );
};

export default CustomerForm;